import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../services/api";

interface QueueFormat {
  sellable_format_id: string;
  format_name: string;
  oz_per_unit: number;
  has_upc: boolean;
}

interface QueueItem {
  product_id: string;
  case_sku: string;
  product_name: string;
  vendor_name: string;
  category: string | null;
  revenue_30d: number;
  formats: QueueFormat[];
}

interface NewItemTask {
  task_key: string;
  sku: string;
  product_name: string;
  qty_sold: number;
  revenue: number;
  first_sale: string;
  last_sale: string;
  answer: { vendor?: string; pack?: string; cost?: string; notes?: string } | null;
}

interface CountTask {
  task_key: string;
  title: string;
  detail: string;
  input: "number" | "text";
  answer: { value?: string } | null;
}

interface PricingTaskFormat {
  format_name: string;
  cash_price: number;
  has_upc: boolean;
}

interface PricingTask {
  product_id: string;
  case_sku: string;
  product_name: string;
  vendor_name: string;
  category: string | null;
  case_configuration: string | null;
  oz_per_case: number;
  current_cost_per_case: number;
  revenue_30d: number;
  needs_rekey: boolean;
  formats: PricingTaskFormat[];
}

const inputStyle: React.CSSProperties = {
  background: "var(--rbw-bg)",
  border: "1px solid var(--rbw-border)",
  color: "var(--rbw-navy)",
};

function fmtMoney(n: number): string {
  return n.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

type Tone = "ok" | "info" | "err";

function useFlash(): [{ text: string; tone: Tone } | null, (text: string, tone?: Tone) => void] {
  const [message, setMessage] = useState<{ text: string; tone: Tone } | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flash = useCallback((text: string, tone: Tone = "ok") => {
    if (timer.current) clearTimeout(timer.current);
    setMessage({ text, tone });
    timer.current = setTimeout(() => setMessage(null), 6000);
  }, []);
  return [message, flash];
}

function FlashBanner({ message }: { message: { text: string; tone: Tone } | null }) {
  if (!message) return null;
  return (
    <div
      className="rounded-xl px-4 py-2.5 mb-4 text-sm font-semibold"
      style={{
        background: message.tone === "err" ? "#FEE2E2" : message.tone === "info" ? "#FEF3C7" : "#D1FAE5",
        color: "var(--rbw-navy)",
      }}
    >
      {message.text}
    </div>
  );
}

// ── Barcodes tab ────────────────────────────────────────────────────────────

function BarcodesTab() {
  const [items, setItems] = useState<QueueItem[]>([]);
  const [totalMissing, setTotalMissing] = useState(0);
  const [doneCount, setDoneCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [message, flash] = useFlash();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/readiness/upc-queue");
      setItems(res.data.items || []);
      setTotalMissing(res.data.total_missing_formats || 0);
      setDoneCount(0);
    } catch {
      flash("Could not load the barcode queue.", "err");
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    load();
  }, [load]);

  const skipUpc = async (item: QueueItem, fmt: QueueFormat) => {
    if (
      !window.confirm(
        `Mark "${fmt.format_name}" of ${item.product_name} as never-scanned?\n\nIt stays sellable — the cashier rings it by search. Use this for formats we basically never sell that way (like a full case). Tell Paula if you hit this by mistake.`
      )
    )
      return;
    setSavingId(fmt.sellable_format_id);
    try {
      await api.post("/readiness/upc-skip", { sellable_format_id: fmt.sellable_format_id });
      setItems((prev) =>
        prev.map((it) =>
          it.product_id !== item.product_id
            ? it
            : { ...it, formats: it.formats.map((f) => (f.sellable_format_id === fmt.sellable_format_id ? { ...f, has_upc: true } : f)) }
        )
      );
      setDoneCount((n) => n + 1);
      flash(`Okay — ${fmt.format_name} won't ask for a barcode again. It still rings by search.`, "info");
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      flash(e.response?.data?.error || "Could not skip that one.", "err");
    } finally {
      setSavingId(null);
    }
  };

  const saveUpc = async (item: QueueItem, fmt: QueueFormat) => {
    const draft = (drafts[fmt.sellable_format_id] || "").trim();
    if (!draft) return;
    setSavingId(fmt.sellable_format_id);
    try {
      const res = await api.post("/readiness/upc", { sellable_format_id: fmt.sellable_format_id, upc: draft });
      setItems((prev) =>
        prev.map((it) =>
          it.product_id !== item.product_id
            ? it
            : { ...it, formats: it.formats.map((f) => (f.sellable_format_id === fmt.sellable_format_id ? { ...f, has_upc: true } : f)) }
        )
      );
      setDoneCount((n) => n + 1);
      setDrafts((d) => ({ ...d, [fmt.sellable_format_id]: "" }));
      const shared = res.data.shared_with || [];
      if (shared.length > 0) {
        flash(
          `Saved. Heads up: this barcode is also on ${shared[0].product_name} (${shared[0].format_name}) — the register will show a picker, which is fine.`,
          "info"
        );
      } else {
        flash(`Saved ${item.product_name} — ${fmt.format_name}.`);
      }
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      flash(e.response?.data?.error || "Could not save that barcode.", "err");
    } finally {
      setSavingId(null);
    }
  };

  const remaining = totalMissing - doneCount;
  const visibleItems = items.filter((it) => it.formats.some((f) => !f.has_upc));

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
        These items sell every week but Karen has no barcode for them. Grab the item off the shelf, scan (or type) its
        barcode into the box, done — it saves straight to the product. Biggest sellers first. (Kegs and crowler fills
        never show here — they have no barcode and ring by search.)
      </p>
      <div className="rounded-2xl px-4 py-3 mb-4 flex items-center justify-between" style={{ background: "var(--rbw-navy)", color: "white" }}>
        <span className="text-sm font-semibold">
          {loading ? "Loading..." : remaining <= 0 ? "All caught up. Nice work!" : `${remaining} barcodes still needed`}
        </span>
        {doneCount > 0 && (
          <span className="text-sm font-bold" style={{ color: "var(--rbw-gold)" }}>
            {doneCount} scanned this session
          </span>
        )}
      </div>
      <FlashBanner message={message} />
      {!loading && visibleItems.length === 0 && (
        <div className="text-center py-16 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
          Nothing in the queue — every active seller has a barcode.
        </div>
      )}
      <div className="space-y-3">
        {visibleItems.map((item, idx) => (
          <div key={item.product_id} className="rounded-2xl p-4 shadow-sm" style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
            <div className="flex items-baseline justify-between gap-3 mb-2">
              <div className="min-w-0">
                <span className="inline-block text-xs font-bold rounded-full px-2 py-0.5 mr-2" style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}>
                  #{idx + 1}
                </span>
                <span className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>{item.product_name}</span>
                <span className="ml-2 text-xs" style={{ color: "var(--rbw-gray-dark)" }}>{item.vendor_name}</span>
              </div>
              <span className="text-xs font-semibold whitespace-nowrap" style={{ color: "var(--rbw-gray-dark)" }}>
                {fmtMoney(item.revenue_30d)} / 30 days
              </span>
            </div>
            {item.formats.map((fmt) =>
              fmt.has_upc ? (
                <div key={fmt.sellable_format_id} className="flex items-center gap-2 py-1 text-sm" style={{ color: "var(--rbw-gray)" }}>
                  <span className="font-bold" style={{ color: "#059669" }}>✓</span>
                  <span>{fmt.format_name}</span>
                </div>
              ) : (
                <div key={fmt.sellable_format_id} className="flex items-center gap-2 py-1">
                  <span className="text-sm w-36 flex-shrink-0" style={{ color: "var(--rbw-navy)" }}>{fmt.format_name}</span>
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Scan barcode here"
                    value={drafts[fmt.sellable_format_id] || ""}
                    onChange={(e) => setDrafts((d) => ({ ...d, [fmt.sellable_format_id]: e.target.value }))}
                    onKeyDown={(e) => {
                      // Scanners send Enter after the digits — that's the save.
                      if (e.key === "Enter") {
                        e.preventDefault();
                        saveUpc(item, fmt);
                      }
                    }}
                    className="flex-1 px-3 py-2 rounded-xl outline-none text-sm"
                    style={inputStyle}
                  />
                  <button
                    onClick={() => saveUpc(item, fmt)}
                    disabled={savingId === fmt.sellable_format_id || !(drafts[fmt.sellable_format_id] || "").trim()}
                    className="px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-40"
                    style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
                  >
                    {savingId === fmt.sellable_format_id ? "..." : "Save"}
                  </button>
                  <button
                    onClick={() => skipUpc(item, fmt)}
                    disabled={savingId === fmt.sellable_format_id}
                    title="We never sell it by scan — stop asking for a barcode. It still rings by search."
                    className="px-3 py-2 rounded-xl text-xs font-bold disabled:opacity-40 whitespace-nowrap"
                    style={{ background: "var(--rbw-bg)", color: "var(--rbw-gray-dark)", border: "1px solid var(--rbw-border)" }}
                  >
                    Don&apos;t need it
                  </button>
                </div>
              )
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── New items tab ───────────────────────────────────────────────────────────

function NewItemsTab() {
  const [tasks, setTasks] = useState<NewItemTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, { vendor: string; pack: string; cost: string; notes: string }>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, flash] = useFlash();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/readiness/new-items");
      const rows: NewItemTask[] = res.data || [];
      setTasks(rows);
      setDrafts(
        Object.fromEntries(
          rows.map((t) => [
            t.task_key,
            {
              vendor: String(t.answer?.vendor || ""),
              pack: String(t.answer?.pack || ""),
              cost: String(t.answer?.cost || ""),
              notes: String(t.answer?.notes || ""),
            },
          ])
        )
      );
    } catch {
      flash("Could not load the new-item list.", "err");
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (t: NewItemTask) => {
    const d = drafts[t.task_key];
    if (!d || (!d.vendor && !d.pack && !d.cost && !d.notes)) return;
    setSavingKey(t.task_key);
    try {
      await api.post("/readiness/answer", { task_key: t.task_key, payload: d });
      setTasks((prev) => prev.map((x) => (x.task_key === t.task_key ? { ...x, answer: d } : x)));
      flash(`Saved info for ${t.product_name}. Paula gets it from here.`);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      flash(e.response?.data?.error || "Could not save.", "err");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
        These items are ringing on the register but Karen doesn&apos;t have them as products yet. Fill in whatever you
        know — which distributor delivers it, the pack (e.g. &quot;4-pack of 16oz cans, 6 packs per case&quot;), and the
        case cost if you can find the invoice. Even partial info helps; a photo of the invoice to Paula is still gold.
      </p>
      <FlashBanner message={message} />
      {loading && <div className="text-center py-12 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Loading...</div>}
      {!loading && tasks.filter((t) => !t.answer).length === 0 && (
        <div className="text-center py-16 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
          No mystery items — everything that sells exists in Karen.
          {tasks.some((t) => t.answer) && " (Answered ones are with Paula.)"}
        </div>
      )}
      {tasks.some((t) => t.answer) && tasks.some((t) => !t.answer) && (
        <div className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
          {tasks.filter((t) => t.answer).length} answered and hidden — Paula turns those into products.
        </div>
      )}
      <div className="space-y-3">
        {tasks.filter((t) => !t.answer).map((t) => (
          <div key={t.task_key} className="rounded-2xl p-4 shadow-sm" style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
            <div className="flex items-baseline justify-between gap-3 mb-1">
              <span className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>
                {t.answer && <span className="mr-1 font-bold" style={{ color: "#059669" }}>✓</span>}
                {t.product_name}
              </span>
              <span className="text-xs font-semibold whitespace-nowrap" style={{ color: "var(--rbw-gray-dark)" }}>
                sku {t.sku} · {fmtMoney(t.revenue)} sold
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
              <input type="text" placeholder="Distributor (e.g. Wilsbach)" value={drafts[t.task_key]?.vendor || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [t.task_key]: { ...d[t.task_key], vendor: e.target.value } }))}
                className="px-3 py-2 rounded-xl outline-none text-sm" style={inputStyle} />
              <input type="text" placeholder="Pack (e.g. 4/16oz cans, 6 per case)" value={drafts[t.task_key]?.pack || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [t.task_key]: { ...d[t.task_key], pack: e.target.value } }))}
                className="px-3 py-2 rounded-xl outline-none text-sm" style={inputStyle} />
              <input type="text" placeholder="Case cost (from invoice)" value={drafts[t.task_key]?.cost || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [t.task_key]: { ...d[t.task_key], cost: e.target.value } }))}
                className="px-3 py-2 rounded-xl outline-none text-sm" style={inputStyle} />
            </div>
            <div className="flex gap-2 mt-2">
              <input type="text" placeholder="Anything else (invoice date, where it lives, etc.)" value={drafts[t.task_key]?.notes || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [t.task_key]: { ...d[t.task_key], notes: e.target.value } }))}
                className="flex-1 px-3 py-2 rounded-xl outline-none text-sm" style={inputStyle} />
              <button
                onClick={() => save(t)}
                disabled={savingKey === t.task_key}
                className="px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-40"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {savingKey === t.task_key ? "..." : t.answer ? "Update" : "Save"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Counts tab ──────────────────────────────────────────────────────────────

function CountsTab() {
  const [tasks, setTasks] = useState<CountTask[]>([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [savingKey, setSavingKey] = useState<string | null>(null);
  const [message, flash] = useFlash();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/readiness/counts");
      const rows: CountTask[] = res.data || [];
      setTasks(rows);
      setDrafts(Object.fromEntries(rows.map((t) => [t.task_key, String(t.answer?.value || "")])));
    } catch {
      flash("Could not load the count list.", "err");
    } finally {
      setLoading(false);
    }
  }, [flash]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (t: CountTask) => {
    const value = (drafts[t.task_key] || "").trim();
    if (!value) return;
    setSavingKey(t.task_key);
    try {
      await api.post("/readiness/answer", { task_key: t.task_key, payload: { value } });
      setTasks((prev) => prev.map((x) => (x.task_key === t.task_key ? { ...x, answer: { value } } : x)));
      flash("Saved — Paula applies these to inventory in one pass.");
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } } };
      flash(e.response?.data?.error || "Could not save.", "err");
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
        A few items need a real human count (their Karen numbers are unreliable for known reasons). Count what&apos;s
        physically there and type it in. The answers are saved for Paula, who applies the inventory fixes — nothing
        changes stock levels by itself.
      </p>
      <FlashBanner message={message} />
      {loading && <div className="text-center py-12 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Loading...</div>}
      {tasks.some((t) => t.answer) && (
        <div className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
          {tasks.filter((t) => t.answer).length} answered and hidden — Paula applies them to inventory.
        </div>
      )}
      {!loading && tasks.every((t) => t.answer) && (
        <div className="text-center py-10 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>All counts answered. Nice work!</div>
      )}
      <div className="space-y-3">
        {tasks.filter((t) => !t.answer).map((t) => (
          <div key={t.task_key} className="rounded-2xl p-4 shadow-sm" style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}>
            <div className="font-semibold text-sm mb-1" style={{ color: "var(--rbw-navy)" }}>
              {t.title}
            </div>
            <p className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>{t.detail}</p>
            <div className="flex gap-2">
              <input
                type="text"
                inputMode={t.input === "number" ? "numeric" : undefined}
                placeholder={t.input === "number" ? "Count" : "Answer"}
                value={drafts[t.task_key] || ""}
                onChange={(e) => setDrafts((d) => ({ ...d, [t.task_key]: e.target.value }))}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    save(t);
                  }
                }}
                className="flex-1 px-3 py-2 rounded-xl outline-none text-sm"
                style={inputStyle}
              />
              <button
                onClick={() => save(t)}
                disabled={savingKey === t.task_key || !(drafts[t.task_key] || "").trim()}
                className="px-4 py-2 rounded-xl text-sm font-bold disabled:opacity-40"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                {savingKey === t.task_key ? "..." : t.answer ? "Update" : "Save"}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Pricing tab (read-only queue, links into Products) ──────────────────────

function PricingTab() {
  const [tasks, setTasks] = useState<PricingTask[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/readiness/pricing-queue");
        setTasks(res.data || []);
      } catch {
        setTasks([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div>
      <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
        Products Karen received from invoices that have a cost but no shelf price yet — the register cannot ring them
        until they&apos;re priced (Patrick&apos;s call; prices are never derived from pack math). Click one to open it
        on the Products page and set the price + selling formats. Ones that are already selling on mPower are at the top.
      </p>
      {loading && <div className="text-center py-12 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>Loading...</div>}
      {!loading && tasks.length === 0 && (
        <div className="text-center py-16 text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
          Everything with a cost has a price. Cutover-clean.
        </div>
      )}
      <div className="space-y-3">
        {tasks.map((t) => {
          const needs: string[] = [];
          if (t.formats.length === 0) {
            needs.push(
              `Create the selling format(s) — how it's actually sold at the register (single / pack / case) — and give each a cash price. The case is ${t.case_configuration || `${t.oz_per_case}oz`}.`
            );
          } else {
            const unpriced = t.formats.filter((f) => !(f.cash_price > 0));
            if (unpriced.length > 0) {
              needs.push(`Set a cash price on: ${unpriced.map((f) => f.format_name).join(", ")}.`);
            }
            const noUpc = t.formats.filter((f) => !f.has_upc);
            if (noUpc.length > 0) {
              needs.push(`Then scan a barcode for: ${noUpc.map((f) => f.format_name).join(", ")} (Barcodes tab picks it up once priced).`);
            }
          }
          if (t.needs_rekey) {
            needs.push(`Its Karen sku (${t.case_sku}) is a placeholder — if the register slip or mPower shows the real item number, note it for Paula.`);
          }
          return (
            <div
              key={t.product_id}
              className="rounded-2xl px-4 py-3 shadow-sm"
              style={{ background: "var(--rbw-bg-card)", border: "1px solid var(--rbw-border)" }}
            >
              <div className="flex items-baseline justify-between gap-3">
                <span className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>
                  {t.product_name}
                  <span className="ml-2 text-xs font-normal" style={{ color: "var(--rbw-gray-dark)" }}>{t.vendor_name} · sku {t.case_sku}</span>
                </span>
                <span className="text-xs font-semibold whitespace-nowrap" style={{ color: "var(--rbw-gray-dark)" }}>
                  {t.revenue_30d > 0 ? `already selling ${fmtMoney(t.revenue_30d)}/30d` : "not selling yet"}
                </span>
              </div>
              <div className="text-xs mt-1 mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
                Our cost: {fmtMoney(t.current_cost_per_case)} per case ({t.case_configuration || `${t.oz_per_case}oz`}).
              </div>
              <ul className="text-xs space-y-1 mb-2" style={{ color: "var(--rbw-navy)" }}>
                {needs.map((n, i) => (
                  <li key={i} className="flex gap-1.5">
                    <span className="font-bold" style={{ color: "var(--rbw-gold)" }}>→</span>
                    <span>{n}</span>
                  </li>
                ))}
              </ul>
              <Link
                to={`/products/${t.product_id}`}
                className="inline-block text-xs font-bold px-3 py-1.5 rounded-xl transition-all hover:opacity-80"
                style={{ background: "var(--rbw-gold)", color: "var(--rbw-navy)" }}
              >
                Open product to set it up →
              </Link>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────────

const TABS = [
  { key: "barcodes", label: "Barcodes" },
  { key: "new-items", label: "New Items" },
  { key: "counts", label: "Counts" },
  { key: "pricing", label: "Needs a Price" },
] as const;

export default function UpcCatchUpPage() {
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("barcodes");

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="font-bold text-2xl mb-1" style={{ color: "var(--rbw-navy)" }}>
        Karen To-Do
      </h1>
      <p className="text-sm mb-4" style={{ color: "var(--rbw-gray-dark)" }}>
        Down-time jobs that get Karen ready to run the register. Everything here saves straight into the system.
      </p>
      <div className="flex gap-2 mb-5 flex-wrap">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="px-4 py-2 rounded-xl text-sm font-bold transition-all"
            style={
              tab === t.key
                ? { background: "var(--rbw-navy)", color: "white" }
                : { background: "var(--rbw-bg-card)", color: "var(--rbw-navy)", border: "1px solid var(--rbw-border)" }
            }
          >
            {t.label}
          </button>
        ))}
      </div>
      {tab === "barcodes" && <BarcodesTab />}
      {tab === "new-items" && <NewItemsTab />}
      {tab === "counts" && <CountsTab />}
      {tab === "pricing" && <PricingTab />}
    </div>
  );
}
